import { Injectable } from '@angular/core';

import { map, Observable } from 'rxjs';

import { HttpServiceHelper } from '@reg/shared/service';
import { IdentityRankingGroup, PagedResponse } from '@reg/shared/model';
import { IJsonApiResponse } from '@reg/shared/json-api/json-api.interface';

/**
 * Service to get ranking groups from identity
 */
@Injectable()
export class IdentityRankingGroupService {
    public static INCLUDE_CHILDREN: string = 'children';
    private readonly identityApiBaseUrl: string;

    constructor(private http: HttpServiceHelper) {
        this.identityApiBaseUrl = http.configService.getBaseConfig('identityApiBaseUrl');
    }

    /**
     * Get a paged list of IdentityRankingGroups
     */
    public getIndex(seasonId: string, includes: string[] = null): Observable<PagedResponse> {
        const apiUrl: string = this.getApiUrl(null, includes, [`seasonId=${seasonId}`]);
        return this.getIndexPagedResponseByUrl(apiUrl);
    }

    /**
     * Get records by a url. This is used for paging since the response provides the proper page urls
     */
    public getIndexPagedResponseByUrl(apiUrl: string): Observable<PagedResponse> {
        return this.http.get(apiUrl).pipe(map((response: IJsonApiResponse) => {
            const json: any = response;
            const rankingGroup: IdentityRankingGroup[] =
                json.data.map((d: any) => new IdentityRankingGroup().deserialize(d, json.included));

            return new PagedResponse(
                json.links.first,
                json.links.last,
                json.links.prev,
                json.links.next,
                json.meta.total,
                rankingGroup,
            );
        }));
    }

    /**
     * Get a single ranking group record
     */
    public get(rankingGroupId: string, include: string[] = null): Observable<IdentityRankingGroup> {
        const apiRequestUrl: string = this.getApiUrl(rankingGroupId, include);

        // Make the http request
        return this.http.get(apiRequestUrl)
            .pipe(map((response: IJsonApiResponse) =>
                new IdentityRankingGroup().deserialize(response, response.included)));
    }

    /**
     * Add or update a ranking group record
     */
    public save(
        rankingGroup: IdentityRankingGroup,
        includes: string[] = null,
        queryParams: string[] = null,
    ): Observable<IdentityRankingGroup> {
        let response: Observable<IJsonApiResponse>;
        const apiUrl: string = this.getApiUrl(rankingGroup.id, includes, queryParams);

        if (rankingGroup.id) {
            // Update existing ranking  group
            response = this.http.patch(apiUrl, rankingGroup.serialize());
        } else {
            // Add a new ranking  group
            response = this.http.post(apiUrl, rankingGroup.serialize());
        }

        return response
            .pipe(
                map((response: IJsonApiResponse) => (new IdentityRankingGroup())
                    .deserialize(response, response.included)),
            );
    }

    /**
     * Delete the specified ranking group.
     */
    public delete(rankingGroupId: string): Observable<boolean> {
        const apiRequestUrl: string = this.getApiUrl(rankingGroupId);
        return this.http.delete(apiRequestUrl).pipe(map(() => true));
    }

    /**
     * Build the api's url based on the given parameters
     */
    private getApiUrl(
        rankingGroupId: string,
        includes: string[] = null,
        queryParams: string[] = null,
    ): string {
        let url: string = `${this.identityApiBaseUrl}/ranking-groups/`;

        if (rankingGroupId) {
            url += rankingGroupId;
        }

        url += this.http.getQueryParameters(includes, queryParams);

        return url;
    }
}
